import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Link } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import { CampaignService } from '../../services/CampaignService';
import campaignStore from '../../stores/CampaignStore';
import liveUsersStore from '../../stores/LiveUsersStore';
import organizationStore from '../../stores/OrganizationStore';
import GeneralModal from '../GeneralModal';

const LiveUserMenu: React.FC<{ campaignId: string }> = observer(({ campaignId }) => {
  const { t: translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { liveUsers } = liveUsersStore;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLeaveCampaign = async () => {
    try {
      const isRemoved = await CampaignService.removeUsersFromCampaign(campaignId);

      if (isRemoved) {
        toast.success(translate('users_dropdown.left_campaign'));
        setTimeout(() => {
          navigate('/campaigns');
        }, 2000);
      } else {
        toast.error(translate('users_dropdown.left_failed'));
      }
    } catch (error) {
      console.error(translate('users_dropdown.left_failed'));
      toast.error(translate('users_dropdown.left_failed'));
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <Box className='relative cursor-pointer flex items-center ml-3' onClick={handleMenuOpen}>
        {liveUsers.slice(0, 2).map((user, index) => (
          <Box
            className='flex items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8 ml-[-8px]'
            key={index}
            sx={{
              zIndex: 10 - index
            }}
          >
            {user.initials}
          </Box>
        ))}

        {liveUsers.length > 3 ? (
          <>
            <Box className='flex items-center justify-center bg-gray-300 text-black rounded-full w-8 h-8 ml-[-9px]'>
              +{liveUsers.length - 2}
            </Box>
            <Box className='ml-1 text-gray-500 ' component='i' sx={{ fontSize: '16px' }} />
          </>
        ) : (
          liveUsers[2] && (
            <Box className='flex items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8 ml-[-8px]'>
              {liveUsers[2].initials}
            </Box>
          )
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        open={Boolean(anchorEl)}
        placement='bottom-end'
        sx={{
          minWidth: '200px',
          maxWidth: '350px',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          overflow: 'auto',
          zIndex: 1300
        }}
      >
        {liveUsers.map((user) => (
          <MenuItem key={user.name}>
            <Box className='flex items-center gap-2'>
              <Box className='flex items-center justify-center bg-blue-500 text-white rounded-full w-8 h-8'>
                {user.initials}
              </Box>
              <Box>{user.name}</Box>
            </Box>
          </MenuItem>
        ))}
        <Box className='flex gap-2 px-3 py-2'>
          <Button
            color='neutral'
            onClick={() => alert(translate('users_dropdown.invite_link'))}
            sx={{
              backgroundColor: 'neutral.solidBg',
              color: 'neutral.solidColor',
              '&:hover': {
                backgroundColor: 'neutral.solidBg',
                color: 'neutral.solidColor'
              },
              width: '100%'
            }}
            variant='outlined'
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link sx={{ marginRight: 1, color: 'neutral.solidColor' }} />
            {translate('users_dropdown.invite_link')}
          </Button>
        </Box>
        <Box
          className='flex gap-2 px-3 py-1'
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2
          }}
        >
          <Button
            color='neutral'
            onClick={handleLeaveCampaign}
            sx={{
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}
            variant='outlined'
          >
            {translate('users_dropdown.leave_campaign')}
          </Button>
          <Button
            color='danger'
            onClick={handleOpenModal}
            sx={{
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}
            variant='outlined'
          >
            {translate('users_dropdown.end_campaign')}
          </Button>
        </Box>
      </Menu>
      <GeneralModal
        actions={
          <>
            <Button color='neutral' onClick={handleCloseModal} variant='plain'>
              {translate('general_button_labels.cancel')}
            </Button>
            <Button
              color='danger'
              onClick={async () => {
                await campaignStore.stopCampaign(campaignId, translate);
                const { selectedOrg } = organizationStore;
                if (selectedOrg) {
                  await campaignStore.fetchCampaigns(selectedOrg, translate);
                }
                liveUsersStore.clearLiveUsers();
                navigate('/campaigns');
                handleCloseModal();
                toast.success('Campaign ended');
              }}
              variant='solid'
            >
              {translate('users_dropdown.end_campaign')}
            </Button>
          </>
        }
        content={
          <Box>
            <Typography level='body-md'>{translate('users_dropdown.end_campaign_confirmation')}</Typography>
          </Box>
        }
        onClose={handleCloseModal}
        open={isModalOpen}
        title={translate('users_dropdown.end_campaign')}
      />
    </>
  );
});

export default LiveUserMenu;
